import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

// Create the Auth Context
const AuthContext = createContext();

// Custom hook to use the Auth Context
export function useAuth() {
  return useContext(AuthContext);
}

// Auth Provider Component
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Register a new user
  async function signup(email, password, name, phone) {
    try {
      setError('');
      // Create user in Firebase Auth
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update user profile with display name
      await updateProfile(result.user, {
        displayName: name
      });
      
      // Create user document in Firestore
      await setDoc(doc(db, 'users', result.user.uid), {
        userId: result.user.uid,
        email: email,
        name: name,
        phone: phone || '',
        role: 'user', // Default role for new users
        createdAt: new Date().toISOString(),
        photoURL: result.user.photoURL || '',
      });
      
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Login an existing user
  async function login(email, password) {
    try {
      setError('');
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Login with Google
  async function loginWithGoogle() {
    try {
      setError('');
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if user document exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      // If user doesn't exist in Firestore, create a new document
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', result.user.uid), {
          userId: result.user.uid,
          email: result.user.email,
          name: result.user.displayName || '',
          phone: result.user.phoneNumber || '',
          role: 'user', // Default role for new users
          createdAt: new Date().toISOString(),
          photoURL: result.user.photoURL || '',
        });
      }
      
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Login with Facebook
  async function loginWithFacebook() {
    try {
      setError('');
      const provider = new FacebookAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if user document exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      // If user doesn't exist in Firestore, create a new document
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', result.user.uid), {
          userId: result.user.uid,
          email: result.user.email,
          name: result.user.displayName || '',
          phone: result.user.phoneNumber || '',
          role: 'user', // Default role for new users
          createdAt: new Date().toISOString(),
          photoURL: result.user.photoURL || '',
        });
      }
      
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Logout the current user
  function logout() {
    setError('');
    return signOut(auth);
  }

  // Reset password
  function resetPassword(email) {
    setError('');
    return sendPasswordResetEmail(auth, email);
  }

  // Update user profile
  async function updateUserProfile(name, phone, photoURL) {
    try {
      setError('');
      if (!currentUser) throw new Error('No user logged in');
      
      // Update auth profile if name provided
      if (name) {
        await updateProfile(currentUser, {
          displayName: name
        });
      }
      
      // Update Firestore document
      const userRef = doc(db, 'users', currentUser.uid);
      const updates = {};
      
      if (name) updates.name = name;
      if (phone) updates.phone = phone;
      if (photoURL) updates.photoURL = photoURL;
      
      await updateDoc(userRef, updates);
      
      // Force refresh of the current user object
      setCurrentUser({...currentUser});
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Check if user is a provider
  async function checkIsProvider() {
    if (!currentUser) return false;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        return userDoc.data().role === 'provider';
      }
      return false;
    } catch (err) {
      console.error("Error checking provider status:", err);
      return false;
    }
  }

  // Set up auth state observer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Get user data from Firestore to include role and other custom fields
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            // Merge Auth user with Firestore data
            setCurrentUser({ ...user, ...userDoc.data() });
          } else {
            setCurrentUser(user);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    loginWithGoogle,
    loginWithFacebook,
    logout,
    resetPassword,
    updateUserProfile,
    checkIsProvider,
    error,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 